import { useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { activarCertificadoApi, type CuerpoActivacion, type RespuestaActivacion } from '@/utils/certificadosApi';

interface Props {
  certificadoId: string;
  /** Texto del botón: «Comprobar y activar» o «Volver a este». */
  etiqueta: string;
  /** Se llama cuando el certificado queda activo (el padre recarga y avisa a la app). */
  onActivado: () => void;
}

type Dialogo = 'sinComprobar' | 'sinVerificar' | null;
type Rechazo = Extract<RespuestaActivacion, { ok: false }>;

/**
 * Botón de activación con el resultado de la comprobación de FACe explicado
 * debajo. Si FACe no responde o no se puede verificar, nunca se activa por
 * sorpresa: las salidas «Activar sin comprobar…» y «Activar igualmente…» piden
 * una confirmación que explica la consecuencia.
 */
export function AccionActivar({ certificadoId, etiqueta, onActivado }: Props) {
  const { fetchWithAuth } = useUser();
  const [activando, setActivando] = useState(false);
  const [rechazo, setRechazo] = useState<Rechazo | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo>(null);

  const enCurso = useRef(false);

  const activar = async (cuerpo: CuerpoActivacion = {}) => {
    // Dos clics en el mismo tick (p. ej. el botón del diálogo durante su animación de salida).
    if (enCurso.current) return;
    enCurso.current = true;
    setDialogo(null);
    setActivando(true);
    setRechazo(null);
    let resultado: RespuestaActivacion;
    try {
      resultado = await activarCertificadoApi(fetchWithAuth, certificadoId, cuerpo);
    } catch {
      resultado = { ok: false, error: 'No se pudo activar el certificado.' };
    } finally {
      enCurso.current = false;
      setActivando(false);
    }

    if (!resultado.ok && resultado.codigo === 'YA_ACTIVO') {
      toast.success('El certificado ya estaba activo.');
      onActivado();
    } else if (resultado.ok) {
      toast.success(
        resultado.comprobacion?.resultado === 'omitida' ?
          'Certificado activado (no tienes FACe configurado, así que no se comprobó).' :
          'Certificado activado correctamente.'
      );
      onActivado();
    } else {
      setRechazo(resultado);
      // Otro certificado se activó mientras decidía: el padre recarga para mostrar el estado real.
      if (resultado.codigo === 'ESTADO_CAMBIADO') onActivado();
    }
  };

  return (
    <div className="space-y-3">
      <Button onClick={() => activar()} disabled={activando}>
        {activando ? 'Comprobando…' : etiqueta}
      </Button>

      {rechazo && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription className="space-y-2">
            <p>
              {rechazo.codigo === 'FACE_RECHAZADO' ? 'FACe todavía no reconoce este certificado: comprueba que está dado de alta en el portal de integradores y asociado a la plataforma.' :
               rechazo.codigo === 'FACE_ERROR' ? 'FACe no responde' :
               rechazo.codigo === 'FACE_NO_VERIFICABLE' ? 'No se puede verificar: aún no tienes facturas registradas en FACe' :
               rechazo.error}
            </p>
            {rechazo.codigo === 'FACE_ERROR' && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => activar()}>Reintentar</Button>
                <Button size="sm" variant="outline" onClick={() => setDialogo('sinComprobar')}>
                  Activar sin comprobar…
                </Button>
              </div>
            )}
            {rechazo.codigo === 'FACE_NO_VERIFICABLE' && (
              <Button size="sm" variant="outline" onClick={() => setDialogo('sinVerificar')}>
                Activar igualmente…
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Dialog open={dialogo !== null} onOpenChange={(abrir) => !abrir && setDialogo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialogo === 'sinComprobar' ? 'Activar sin comprobar' : 'Activar igualmente'}</DialogTitle>
            <DialogDescription>
              {dialogo === 'sinComprobar' ?
                'No se ha podido comprobar el certificado con FACe. Si aún no está dado de alta en el portal de integradores y asociado a la plataforma, FACe rechazará los envíos hasta que lo esté.' :
                'No hay ninguna factura registrada en FACe con la que comprobar el certificado. Si no está dado de alta, FACe rechazará los envíos hasta que lo esté.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogo(null)}>Cancelar</Button>
            {dialogo === 'sinComprobar' ? (
              <Button onClick={() => activar({ sinComprobar: true, confirmacion: 'ACTIVAR_SIN_COMPROBAR' })}>
                Activar sin comprobar
              </Button>
            ) : (
              <Button onClick={() => activar({ confirmacion: 'ACTIVAR_SIN_VERIFICAR' })}>
                Activar igualmente
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
